import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useSimpleSearch } from '../../utils/useSimpleSearch';
import { useDebounce } from '../../utils/useServerFiltering';
import { 
  SearchableClientDropdownProps, 
  ClientOption, 
  MIN_SEARCH_LENGTH, 
  SEARCH_DEBOUNCE_DELAY, 
  MAX_SEARCH_RESULTS 
} from './config';

const SearchableClientDropdown: React.FC<SearchableClientDropdownProps> = ({
  value,
  onChange,
  placeholder = "Search clients...",
  className = '',
  disabled = false,
  allowNone = true,
  noneLabel = 'All Clients',
  required = false
}) => {
  const supabase = useSupabaseClient();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [displayText, setDisplayText] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemsContainerRef = useRef<HTMLDivElement>(null);
  const fetchedValueRef = useRef<number | string | null | undefined>(null); // Track what value we've fetched for

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_DELAY);

  // Use our search hook
  const { data: clients, loading } = useSimpleSearch<ClientOption>(
    'search_clients',
    debouncedSearchTerm,
    MIN_SEARCH_LENGTH,
    MAX_SEARCH_RESULTS
  );



  // Create dropdown items (copy exact structure from Dropdown component)
  const dropdownItems = useMemo(() => {
    const clientItems = clients.map(client => ({
      id: client.id,
      label: client.company_name,
      value: client.id.toString()
    }));

    if (allowNone) {
      return [
        {
          id: 'none',
          label: noneLabel,
          value: null
        },
        ...clientItems
      ];
    }

    return clientItems;
  }, [clients, allowNone, noneLabel]);

  // Build visible items list for keyboard navigation
  const visibleItems = useMemo(() => {
    const items: Array<{ id: string | number; label: string; value: string | null }> = [];
    
    // When no search or search too short, show "All Clients" first
    if (searchTerm.length < MIN_SEARCH_LENGTH) {
      if (allowNone) {
        items.push({ id: 'none', label: noneLabel, value: null });
      }
    } else if (!loading) {
      // During search, show "All Clients" first, then results
      if (allowNone) {
        items.push({ id: 'none', label: noneLabel, value: null });
      }
      dropdownItems
        .filter(item => item.value !== null)
        .forEach(item => items.push(item));
    }
    
    return items;
  }, [searchTerm, allowNone, noneLabel, loading, dropdownItems]);

  // Reset highlighted index when dropdown opens or items change
  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(-1);
    }
  }, [isOpen, visibleItems.length]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && itemsContainerRef.current) {
      const container = itemsContainerRef.current;
      const highlightedElement = container.querySelector(`[data-index="${highlightedIndex}"]`) as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Handle selection (copy exact logic from Dropdown component)
  const handleSelect = useCallback((item: { id: string | number; label: string; value: string | null }) => {
    // Store display text for selected item
    setDisplayText(item.label);
    fetchedValueRef.current = item.value; // Mark as fetched to prevent refetch
    
    onChange(item.value, null); // Use the same signature as Dropdown
    setIsOpen(false);
    setSearchTerm(''); // Clear search to show selected value
  }, [onChange]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen) {
      // Open dropdown on arrow down or enter
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!disabled) setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < visibleItems.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < visibleItems.length) {
          handleSelect(visibleItems[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
      case 'Tab':
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  }, [isOpen, disabled, highlightedIndex, visibleItems, handleSelect]);

  // Fetch client name when value changes (for initial load or external changes)
  useEffect(() => {
    // If value is null, show the none label
    if (value === null) {
      setDisplayText(noneLabel);
      fetchedValueRef.current = null;
      return;
    }
    
    // If we've already fetched for this value, don't fetch again
    if (fetchedValueRef.current === value) {
      return;
    }
    
    // Fetch the client name for the given value
    const fetchClientName = async () => {
      try {
        const clientId = typeof value === 'string' ? parseInt(value, 10) : value;
        
        const { data, error } = await supabase
          .from('clients')
          .select('company_name')
          .eq('id', clientId)
          .single();
        
        if (error) {
          console.error('Error fetching client name:', error);
          return;
        }
        
        if (data) {
          setDisplayText(data.company_name);
          fetchedValueRef.current = value;
        }
      } catch (err) {
        console.error('Error fetching client name:', err);
      }
    };
    
    fetchClientName();
  }, [value, noneLabel, supabase]);

  // Get display value for selected item
  const displayValue = displayText || placeholder;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm(''); // Clear search when closing
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`dropdown-container ${className} ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      <div 
        className={`dropdown-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls="client-dropdown-listbox"
      >
        <div className="selected-value">
          {displayValue || placeholder}
        </div>
        <div className="dropdown-arrow">
          <svg 
            width="12" 
            height="8" 
            viewBox="0 0 12 8" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            className={isOpen ? 'rotated' : ''}
          >
            <path 
              d="M1 1.5L6 6.5L11 1.5" 
              stroke="currentColor" 
              strokeWidth="1.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {isOpen && (
        <div className="dropdown-menu">
          {/* Search input inside dropdown */}
          <div className="search-container">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search clients..."
              className="search-input dropdown-search-input"
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>

          {/* Items container */}
          <div className="items-container" ref={itemsContainerRef} role="listbox" id="client-dropdown-listbox">
            {/* Show "All Clients" when no search or search too short */}
            {searchTerm.length < MIN_SEARCH_LENGTH && allowNone && (
              <div
                data-index={0}
                className={`dropdown-item ${value === null ? 'selected' : ''} ${highlightedIndex === 0 ? 'highlighted' : ''}`}
                onClick={() => handleSelect({ id: 'none', label: noneLabel, value: null })}
                onMouseEnter={() => setHighlightedIndex(0)}
                role="option"
                aria-selected={value === null}
              >
                {noneLabel}
              </div>
            )}

            {/* Show search hint */}
            {searchTerm.length > 0 && searchTerm.length < MIN_SEARCH_LENGTH && (
              <div className="no-results">
                Type {MIN_SEARCH_LENGTH - searchTerm.length} more character{searchTerm.length === MIN_SEARCH_LENGTH - 1 ? '' : 's'} to search...
              </div>
            )}

            {/* Show loading */}
            {loading && searchTerm.length >= MIN_SEARCH_LENGTH && (
              <div className="no-results">Searching clients...</div>
            )}

            {/* Show search results */}
            {!loading && searchTerm.length >= MIN_SEARCH_LENGTH && (
              <>
                {/* Include "All Clients" option in search results too */}
                {allowNone && (
                  <div
                    data-index={0}
                    className={`dropdown-item ${value === null ? 'selected' : ''} ${highlightedIndex === 0 ? 'highlighted' : ''}`}
                    onClick={() => handleSelect({ id: 'none', label: noneLabel, value: null })}
                    onMouseEnter={() => setHighlightedIndex(0)}
                    role="option"
                    aria-selected={value === null}
                  >
                    {noneLabel}
                  </div>
                )}
                
                {/* Client results */}
                {dropdownItems.length > 0 ? (
                  dropdownItems
                    .filter(item => item.value !== null) // Exclude the "none" option since we show it separately
                    .map((item, index) => {
                      const itemIndex = allowNone ? index + 1 : index;
                      return (
                        <div
                          key={item.id}
                          data-index={itemIndex}
                          className={`dropdown-item ${item.value === value ? 'selected' : ''} ${highlightedIndex === itemIndex ? 'highlighted' : ''}`}
                          onClick={() => handleSelect(item)}
                          onMouseEnter={() => setHighlightedIndex(itemIndex)}
                          role="option"
                          aria-selected={item.value === value}
                        >
                          {item.label}
                        </div>
                      );
                    })
                ) : (
                  <div className="no-results">No clients found matching "{searchTerm}"</div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableClientDropdown;

export {}; 